import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Stepper, Step } from 'react-form-stepper';
import Layout from 'components/Common/Layout';
import { useToastContext, FloorPlanFileSource } from 'auto-design-common';
import { useAppContext } from 'components/AppContext';
import * as api from 'utils/api';
import { useModalContext } from 'components/ModalContext';
import { ModalKey } from 'constants/modal';
import useTranslation from 'hooks/useTranslation';
import { ReactComponent as CoinIcon } from 'assets/images/Auto-Coin.svg';
import Packages from 'components/CreateFloorPlan/Packages';
import FilesSelection from 'components/CreateFloorPlan/FilesSelection';
import Information from 'components/CreateFloorPlan/Information';
import { getPriceKey } from 'components/CreateFloorPlan/utils';
import styles from 'components/CreateFloorPlan/CreateFloorPlan.module.scss';
import { useQuery } from '@tanstack/react-query';
import { getFileUrl } from 'utils/file';

const Screen = {
  PACKAGES: 0,
  FILE_SELECTION: 1,
  INFORMATION: 2,
};

export default function CompleteInformation({
  floorPlan,
}) {
  const { data: allServerFiles } = useQuery({
    queryKey: ['floor-plan', floorPlan.id, 'floor-plan-files'],
    queryFn: () => api.getFloorPlanFiles(floorPlan.id),
  });
  const { tCreateFloorPlan } = useTranslation();
  const { configs } = useAppContext();
  const { toastError } = useToastContext();
  const [screen, setScreen] = useState(Screen.PACKAGES);
  const [packages, setPackages] = useState([]);
  const [files, setFiles] = useState([]);
  const [urls, setUrls] = useState([]);
  const [info, setInfo] = useState({
    name: floorPlan.name,
    floors: floorPlan.metadata.floors,
    userNotes: floorPlan.userNotes,
  });
  const { getInfo, request } = useAppContext();
  const { showModal } = useModalContext();
  const [submitting, setSubmitting] = useState(false);
  const history = useHistory();

  const serverFiles = useMemo(() => allServerFiles?.filter(f => f.source === FloorPlanFileSource.USER_UPLOAD) || [], [allServerFiles]);

  useEffect(() => {
    if (!serverFiles) {
      return;
    }

    const urls = serverFiles.filter(f => f.type === 'url')
      .map(u => ({
        id: u.id,
        url: u.fileUrl,
        readOnly: true,
      }));
    setUrls(urls);

    const files = serverFiles.filter(f => f.type !== 'url')
      .map(f => ({
        id: f.id,
        file: {
          name: f.name,
        },
        downloadUrl: getFileUrl(f.fileId),
        readOnly: true,
      }));
    setFiles(files);
  }, [serverFiles]);

  const price = useMemo(() => {
    const base = packages.reduce((acc, p) => acc + configs[getPriceKey(p)], 0);

    return base * info.floors;
  }, [packages, configs, info.floors]);

  const onSubmit = useCallback(async () => {
    try {
      setSubmitting(true);
      const { success, error } = await request(api.updateFloorPlan(floorPlan.id, {
        name: info.name,
        packages,
        fileUrls: urls.map(u => ({
          type: 'url',
          url: u.url,
        })),
        floors: info.floors,
        userNotes: info.userNotes,
      }));

      if (!success) {
        throw error;
      }

      // eslint-disable-next-line guard-for-in
      for (const file of files) {
        if (!file.readOnly) {
        // eslint-disable-next-line no-await-in-loop
          const { key } = await api.uploadFile(file.file, false);
          // eslint-disable-next-line no-await-in-loop
          const { error } = await request(api.uploadFloorPlanFile(floorPlan.id, {
            fileKey: key,
            fileType: file.file.type,
            fileName: file.file.name,
          }));
          if (error) {
            throw error;
          }
        }
      }

      const { error: finishUploadError } = await request(api.finishUploadFloorPlan(floorPlan.id));

      if (finishUploadError) {
        throw finishUploadError;
      }

      getInfo();
      showModal(ModalKey.FILES_UPLOADED, {
        onClose: () => {
          history.push(`/floor-plans/${floorPlan.id}`);
        },
      });
    } catch (e) {
      toastError(e);
    }

    setSubmitting(false);
  }, [getInfo, info, request, toastError, urls, files, showModal, packages, history]);

  return (
    <Layout>
      <div className={styles.totalPrice}>
        {tCreateFloorPlan('totalPrice')}
        {' '}
        <span className={styles.number}>
          {price}
          {' '}
          x
          {' '}
        </span>
        <CoinIcon />
      </div>
      <div className={styles.stepperWrapper}>

        <div className={styles.stepper}>
          <Stepper
            activeStep={screen}
            styleConfig={{
              activeBgColor: '#ff9c34',
              completedBgColor: '#fcb86f',
            }}
            connectorStyleConfig={{
              activeColor: '#fcb86f',
              completedColor: '#fcb86f',
            }}
            connectorStateColors
          >
            <Step
              label="Packages"
              onClick={() => {
                setScreen(Screen.PACKAGES);
              }}
            />
            <Step
              label="Files"
              onClick={() => {
                setScreen(Screen.FILE_SELECTION);
              }}
            />
            <Step label="Information" />
          </Stepper>
        </div>
      </div>

      {screen === Screen.PACKAGES && (
        <Packages
          packages={packages}
          setPackages={setPackages}
          onContinue={() => {
            setScreen(Screen.FILE_SELECTION);
          }}
        />
      )}

      {screen === Screen.FILE_SELECTION && (
        <FilesSelection
          files={files}
          setFiles={setFiles}
          urls={urls}
          setUrls={setUrls}
          onContinue={() => {
            setScreen(Screen.INFORMATION);
          }}
        />
      )}
      {screen === Screen.INFORMATION && (
        <Information
          packages={packages}
          setPackages={setPackages}
          files={files}
          urls={urls}
          info={info}
          setInfo={setInfo}
          onSubmit={onSubmit}
          submitting={submitting}
        />
      )}
    </Layout>
  );
}
